import BackButton from "../components/BackButton";
import { BurgerHintSystem } from "../components/burger/BurgerHintSystem";
import { BurgerManager } from "../components/burger/BurgerManager";
import { Countdown } from "../components/burger/Countdown";
import Hand from "../components/burger/Hand";
import { IngredientConfig, IngredientSpawner } from "../components/burger/IngredientSpawner";
import { Recipe, RecipeManager } from "../components/burger/RecipeManager";
import ContinueButton from "../components/ContinueButton";
import TryAgainButton from "../components/TryAgainButton";
import { CONST } from "../CONST";
import { MinigameScene } from "./generic/MinigameScene";

export enum IngredientsEnum{
    Egg = 1,
    Lettuce,
    Cheese,
    Tomato,
    Patty,
    BunBottom,
    BunTop,
}

export const FIRST_BURGER_COMPLETED_KEY = "firstBurgerCompleted";

const minigameConfig = {
   ingredients: [
       {ingredient: IngredientsEnum.Egg, 
        spawn: {x: 110, y: 419},
        texture: CONST.BURGER.I_EGGS,
        shadow_texture: CONST.BURGER.I_S_EGGS,
        height: 10,
        depth: 5,
        origin: {x: 0.5, y: 0.5},
        },
        {ingredient: IngredientsEnum.Lettuce, 
            spawn: {x: 309, y: 375},
            texture: CONST.BURGER.I_LETTUCE,
            shadow_texture: CONST.BURGER.I_S_LETTUCE,
            height: 10,
            depth: 5,
            origin: {x: 0.5, y: 0.5},
        },
        {ingredient: IngredientsEnum.Cheese, 
            spawn: {x: 196, y: 343},
            texture: CONST.BURGER.I_CHEESE,
            shadow_texture: CONST.BURGER.I_S_CHEESE,
            height: 5,
            depth: 5,
            origin: {x: 0.5, y: 0.37},
        },
        {ingredient: IngredientsEnum.Tomato, 
            spawn: {x: 199, y: 248},
            texture: CONST.BURGER.I_TOMATO,
            shadow_texture: CONST.BURGER.I_S_TOMATO,
            height: 7,
            depth: 5,
            origin: {x: 0.5, y: 0.5},
        },
        {ingredient: IngredientsEnum.Patty, 
            spawn: {x: 263, y: 167},
            texture: CONST.BURGER.I_PATTY,
            shadow_texture: CONST.BURGER.I_S_PATTY,
            height: 15,
            depth: 5,
            origin: {x: 0.5, y: 0.5},
        },
        {ingredient: IngredientsEnum.BunBottom, 
            spawn: {x: 72, y: 317},
            texture: CONST.BURGER.I_BUN_BOTTOM,
            shadow_texture: CONST.BURGER.I_S_BUN_BOTTOM,
            height: 12,
            depth: 5,
            origin: {x: 0.5, y: 0.5},
        },
        {ingredient: IngredientsEnum.BunTop, 
            spawn: {x: 50, y: 260},
            texture: CONST.BURGER.I_BUN_TOP,
            shadow_texture: CONST.BURGER.I_S_BUN_TOP,
            height: 12,
            depth: 6,
            origin: {x: 0.5, y: 0.5},
        }
   ]
}

export class BurgerScene extends MinigameScene {

    private isRestart = false;
    private isFirstBurger = true;

    private endScoreDisplay !: Phaser.GameObjects.Text;
    private highscoreImage !: Phaser.GameObjects.Image;
    private burgerImage !: Phaser.GameObjects.Image;

    private burger !: BurgerManager;
    private recipe !: RecipeManager;
    private ingredients : IngredientSpawner[] = [];
    private countdown !: Countdown;
    private hand !: Hand;
    
    private hintSystem !: BurgerHintSystem;

    private serveButton !: ServeButton;
    private trashButton !: ServeButton;

    private serveAnim !: ServeAnim;
    private trashAnim !: TrashAnim;

    private currentScore = 0;

    private difficulty =  { //CONTROLLED BY REMOTECONFIG SCENE!
        baseTime: 3000,
        defaultTimePerIngredient: 3000,
        minimumTimePerIngredient: 1000,
        durationMultiplier: 0.96, //each burger ingredient duration is durationMultiplier*previousDuration
        firstBurgerTime: 120000,
    };


    constructor(){
        super ({
            sceneName: CONST.SCENES.BURGER,
            duration: 999999999,
            showCountdown: 0,
            showHomeButton: false,
            timeToStart: 1000,
            startScreenColor: 0xFF9900,
            startText: "BURGER\nMADNESS!",
            startTextYOrigin: 0.45,
            startTextColor: 0x323232,
            endScreen: CONST.BURGER.END,
            showSkipButton: false,
            nextScene: CONST.SCENES.BIRTH,
            endButtons: {
                normalNoSkip: {x: -426.5, y: 386, scale: 1.30},
                normalWithSkip: {x: 283, y: 386, scale: 1.30},
                skip: {x: 570, y: 386, scale: 1.30}
            },
            isArcadeMinigame: true,
          })
    }

    _init(){
        //Reset all class variables that change during scene here (including arrays that initialize in create!)
        this.ingredients = [];
        this.currentScore = 0;

        this.isRestart = this.checkForRestart();

        this.getRemoteConfig();

        this.isFirstBurger = !JSON.parse(this.storagePlugin.getItem(FIRST_BURGER_COMPLETED_KEY, "false"));
    }

    private getRemoteConfig(){
        let remoteConf = this.remoteConf.getConfig("gB").split(";");
        this.difficulty.durationMultiplier = parseFloat(remoteConf[0]);
        this.difficulty.baseTime = parseInt(remoteConf[1]) * 100;
        this.difficulty.defaultTimePerIngredient = parseInt(remoteConf[2]) * 100;
        this.difficulty.minimumTimePerIngredient = parseInt(remoteConf[3]) * 100;
    }

    private checkForRestart(): boolean{
        let key = this.sceneName + "restart";
        let restartInfo = this.storagePlugin.getItem(key, "");
        if (restartInfo){
            let parsed = JSON.parse(restartInfo);
            this.currentScore = parsed.score;

            this.storagePlugin.removeItem(key);
            return true;
        }

        return false;
    }


    _preload(){
    }

    _create(){
        this.createBackground();

        this.createRecipe();
        this.createBurger();
        this.createIngredients();
        this.createButtons();
        this.createCountdown();

        this.hand = new Hand(this);
        this.serveAnim = new ServeAnim(this);
        this.trashAnim = new TrashAnim(this);

        this.hintSystem = new BurgerHintSystem(this, minigameConfig.ingredients);    
    }

    createBackground() {
        this.add.image(0, 0, CONST.BURGER.BACKGROUND).setOrigin(0, 0).setDepth(0);
        this.add.image(0, 0, CONST.BURGER.ROPE).setOrigin(0, 0).setDepth(60);
        this.add.image(427, 218, CONST.BURGER.BOARD).setDepth(1);
    } 

    createRecipe(){
        this.recipe = new RecipeManager(this);
    }

    createBurger(){
        this.burger = new BurgerManager(this);
    }

    createIngredients(){
        minigameConfig.ingredients.forEach(ingConf => {
            let ing  = new IngredientSpawner(ingConf, this.burger, this);
            this.ingredients.push(ing);
        });
    }

    createButtons(){
        this.serveButton = new ServeButton(728, 329, -38, - 34, CONST.BURGER.PLATE_OUTLINE, CONST.BURGER.PLATE, () => {this.tryServeBurger()}, this);
        this.trashButton = new ServeButton(89.5, 131, 59, - 1, CONST.BURGER.TRASH_OUTLINE, CONST.BURGER.TRASH, () => {this.tryRecycleBurger()}, this);
    }

    createCountdown(){
        this.countdown = new Countdown({scene: this, position: {x: 85, y: 47}, onComplete: () => {this.onCountdownCompleted()}});
    }

    onCountdownCompleted(){
        this.sendBurgerAnalyticsEvent(false, true);
        this.triggerLose();
    }

    onIngredientDrag(start: boolean){
        this.hintSystem.handleDrag(start);
    }

    tryServeBurger(){
        if (!this.countdown.isCountingDown) return;
        this.forceShowButtonOutline(false);

        
        if (this.recipe.isBurgerCompleted(this.burger)){
            setTimeout(() => {
                this.sound.play(CONST.AUDIO.WIN, {detune: this.getDetune()});
            }, 400);
            this.sendBurgerAnalyticsEvent(true);

            this.currentScore++;
            this.showNextRecipe();


            this.storagePlugin.setItem(FIRST_BURGER_COMPLETED_KEY, true.toString());
        } else {
            this.sendBurgerAnalyticsEvent(false, false);

           this.triggerLose();
        }

        this.hintSystem.handleButtonClick();

        this.burger.serve();
        this.serveAnim.animate();
    }

    tryRecycleBurger(){
        this.forceShowButtonOutline(false);
        this.hintSystem.handleButtonClick();
        this.burger.recycle();
        this.trashAnim.animate();
    }

    isBurgerCompleted(){ //True if burger has all correct ingredients
      return this.recipe.isBurgerCompleted(this.burger);
    }

    isBurgerCorrect(){ //True if burger has only correct ingredients, even if not all ingredients are added yet
        return this.recipe.isBurgerCorrect(this.burger);
    }

    getNextIngredient(){
        return this.recipe.getNextIngredient(this.burger);
    }

    showNextRecipe(){
        this.forceShowButtonOutline(false);
        var recipe = this.recipe.nextRecipe(750, this.currentScore, this.isFirstBurger);
        var nextDuration = this.getNextBurgerDuration(recipe);
        if (this.isFirstBurger && this.currentScore == 0){
            nextDuration = this.difficulty.firstBurgerTime;
        }

        this.countdown.restart(nextDuration, 1750);
    }

    getNextBurgerDuration(recipe: Recipe){
        let progressMultiplier = Math.pow(this.difficulty.durationMultiplier, this.currentScore);
        let tpi = this.difficulty.defaultTimePerIngredient * progressMultiplier;
        
        return this.difficulty.baseTime + recipe.ingredients.length * Math.max(tpi, this.difficulty.minimumTimePerIngredient); 
    }

    _update(time: number, delta: number){
            this.checkBurgerButtonDistance();

            this.hintSystem.onUpdate(delta);
    }

    onIngredientAdded(length: number) {
        if (length >= this.recipe.getCurrentRecipeLength()){
            this.forceShowButtonOutline(true);
        }
    }

    forceShowButtonOutline(show: boolean){
        this.serveButton.forceFade(show);
        this.trashButton.forceFade(show);
    }

    checkBurgerButtonDistance(){
        this.serveButton.checkBurgerVicinity(this.burger.getBurgerPosition().x, this.burger.getBurgerPosition().y, !this.burger.isBurgerDragging());
        this.trashButton.checkBurgerVicinity(this.burger.getBurgerPosition().x, this.burger.getBurgerPosition().y, !this.burger.isBurgerDragging());
    }
    
    onGameStarted(){
        this.showNextRecipe();
    }
        
    _timeout(){
    }

    _afterLoss(){
    }

    _afterWin(){
    }

    private triggerLose(){
        setTimeout(() => {
            this.sound.play(CONST.AUDIO.LOSE, {volume: 4});
        }, 500);

        this.sendFinalScoreAnalyticsEvent(this.currentScore);
        this.saveAndDisplayScore();

        this.countdown.pause();
        this.ingredients.forEach(ing => ing.disable());
        this.hintSystem.handleEndGame();

        this.serveButton.disableInteractive();
        this.trashButton.disableInteractive();

        setTimeout(() => {
            this.hand.setFollow(false);

            this.add.graphics()
            .fillStyle(0x535353, 0.33)
            .fillRect(0, 0, this.game.renderer.width, this.game.renderer.height)
            .setDepth(500);
    
        }, 2000);   

        this.loseGame(2000);
    }

    private triggerWin(){
        //not used in arcade
    }

    private saveAndDisplayScore(){
        var score = this.currentScore;
        var oldHighscore = this.getHighscore();
        var isNewHighscore = score > oldHighscore;

        this.displayScore(score, isNewHighscore, oldHighscore);

        if (isNewHighscore){
            this.setHighscore(score);
        }
    }

    private displayScore(score: number, isHighscore: boolean, oldHighscore: number){
        this.endScoreDisplay.text = score.toString();
        let scoreLength =  this.endScoreDisplay.text.length;
        this.burgerImage.setPosition(this.burgerImage.x +  scoreLength * 20, this.burgerImage.y - scoreLength * 1);
        this.highscoreImage.setPosition(this.burgerImage.x + 54, this.burgerImage.y + 17);
        this.highscoreImage.setAlpha(isHighscore ? 1 : 0);
    }

    protected getCustomLoseUIElements() : object[] {
        this.endScoreDisplay = this.add.text(525, 200, "123",
                     {fontFamily: 'bloggerSans', fontSize: 35, color: "#ED8340", align: 'center'})
                     .setAngle(-4).setDepth(2);

        this.burgerImage = this.add.image(600, 214, CONST.BURGER.BURGERS_TEXT).setScale(0.55).setDepth(2);

        this.highscoreImage = this.add.image(680, 225, CONST.BURGER.NEW_BEST).setDepth(2);

        if (this.isRestart || this.registry.get(CONST.REGISTRY.ADBLOCKENABLED)){
            //Without continue button
            var backButton2 = new BackButton(CONST.SCENES.MENU, 515, 375, this, CONST.BURGER.ARCADE_BUTTON).setAngle(0).setScale(0.55).setDepth(2);
            var tryAgainButton2 = new TryAgainButton(660, 367, this, CONST.BURGER.TRY_AGAIN_BUTTON).setAngle(0).setScale(0.55).setDepth(2);
            return [backButton2, tryAgainButton2, this.endScoreDisplay, this.burgerImage, this.highscoreImage];

        } else {
            //With continue button
            var backButton = new BackButton(CONST.SCENES.MENU, 588, 412, this, CONST.BURGER.BACK_TO_ARCADE).setScale(0.55);
            var continueButton = new ContinueButton(663, 356, this, CONST.BURGER.CONTINUE_BUTTON, () => {return this.getContinueData()}).setScale(0.55);
            var tryAgainButton = new TryAgainButton(515, 364, this, CONST.BURGER.TRY_AGAIN_BUTTON_2).setScale(0.55);
            return [backButton, continueButton, tryAgainButton, this.endScoreDisplay, this.burgerImage, this.highscoreImage];
        }
    }

    getContinueData(){
        return {score: this.currentScore};
    }

    private sendBurgerAnalyticsEvent(completed: boolean, isTimeout: boolean = false){
        let eventName = completed ? "Completed" : (isTimeout ? "TooSlow" : "Wrong");
        let burgerName = this.recipe.getCurrentRecipeName();
        let value = completed ? this.countdown.getElapsedTime() : this.countdown.getTotalTime();

        const customDesignEvent = this.sceneName + ":" + burgerName + ":" + eventName;
        this.analyticsPlugin.addDesignEventWithValue(customDesignEvent, Math.round(value));
        // console.log("burger event " + eventName + " " + burgerName + " " + value);
    }
}


class ServeButton extends Phaser.GameObjects.Image {
    private outline : Phaser.GameObjects.Image;
    private fadeTween !: Phaser.Tweens.Tween;

    private isEnabled = true;

    private onClickFun: () => void;

    private maxBurgerDistance = 100;

    private isHovering = false;
    private isForcedFadeIn = false;
    private isBurgerClose = false;

    private outlineVisible = false;

    constructor(x: number, y: number, xOffset: number, yOffset: number, outlineTexture: string, texture: string, onClick: () => void, scene: BurgerScene, ){
        super(scene, x, y, texture);

        this.outline = scene.add.image(x +xOffset, y + yOffset, outlineTexture).setAlpha(0);
        this.onClickFun = onClick;

        this.setInteractive({useHandCursor: true});
        this.on("pointerup", ()=> {
            if (!this.isEnabled) return;

            onClick();

            this.isEnabled = false;
            setTimeout(() => {
                this.isEnabled = true;
            }, 1500);
        });

        this.on("pointerover", ()=> {
            if (!this.isEnabled) return;

            this.isHovering = true;
            this.updateOutline();
        });

        this.on("pointerout", ()=> {
            this.isHovering = false;
            this.updateOutline();
        });

        scene.add.existing(this);
    }

    public checkBurgerVicinity(burgerX: number, burgerY: number, dropped: boolean){
        if (Phaser.Math.Distance.Between(this.x, this.y, burgerX, burgerY) < this.maxBurgerDistance){
            this.isBurgerClose = true;
            this.updateOutline();

            if (dropped && this.isEnabled){
                this.onClickFun();
            }
        } else {
            this.isBurgerClose = false;

            this.updateOutline();
        }

    }

    private updateOutline(){
        let visible = this.isHovering || this.isForcedFadeIn || this.isBurgerClose;

        if (this.outlineVisible != visible){
            this.outlineVisible = visible;
            this.fadeOutline(this.outlineVisible);
        }
    }

    private fadeOutline(fadeIn: boolean){
        if (this.fadeTween){
            this.fadeTween.stop();
        }

        this.fadeTween = this.scene.tweens.add({
            targets: this.outline, 
            alpha: fadeIn ? 1 : 0,
            duration: 200,
            ease: "Quad.easeOut",
        });
    }

    public forceFade(fadeIn: boolean){
        this.isForcedFadeIn = fadeIn;
        this.updateOutline();
    }
}

class ServeAnim {
    private scene: Phaser.Scene;

    private cont: Phaser.GameObjects.Container;

    private tween !: Phaser.Tweens.Tween;

    private conf = {
        move: {x: 427, y: 240},
        startPos: {x: 427, y: -75},
        ease: "Back.easeInOut",
        duration: 500,
        hold: 200,
    }

    constructor(scene: Phaser.Scene){
        this.scene = scene;

        let plate = scene.add.image(0, 0, CONST.BURGER.PLATE);
        let hand = scene.add.image(19, -339, CONST.BURGER.HAND).setAngle(90);

        this.cont = scene.add.container(this.conf.startPos.x, this.conf.startPos.y).setDepth(9);
        this.cont.add([plate, hand]);
    }

    public animate(){
        if (this.tween){
            this.tween.stop();
        }

        this.tween = this.scene.tweens.add({
            targets: this.cont,
            x: this.conf.move.x,
            y: this.conf.move.y,
            ease: this.conf.ease,
            duration: this.conf.duration,
            hold: this.conf.hold,
            yoyo: true,
        });
    }
}

class TrashAnim {
    private scene: BurgerScene;

    private bin: Phaser.GameObjects.Image;
    private bin_front: Phaser.GameObjects.Image;

    private tween !: Phaser.Tweens.Tween;

    private conf = {
        move: {x: 424, y: 400},
        startPos: {x: 424, y: 590},
        ease: "Back.easeInOut",
        duration: 500,
        hold: 500,
    }

    constructor(scene: BurgerScene){
        this.scene = scene;

        this.bin = scene.add.image(this.conf.startPos.x, this.conf.startPos.y, CONST.BURGER.TRASH).setScale(1.5).setDepth(7);     
        this.bin_front = scene.add.image(this.conf.startPos.x, this.conf.startPos.y, CONST.BURGER.TRASH_TOP).setScale(1.5).setDepth(11).setOrigin(0.49, 0.49);
    }

    public animate(){
        this.scene.sound.play(CONST.AUDIO.TRASH, {volume: 8});

        if (this.tween){
            this.tween.stop();
        }

        this.tween = this.scene.tweens.add({
            targets: [this.bin, this.bin_front],
            x: this.conf.move.x,
            y: this.conf.move.y,
            ease: this.conf.ease,
            duration: this.conf.duration,
            hold: this.conf.hold,
            yoyo: true,
        });
    }
}